/**
 * Native Stripe Payment Sheet helper — thin wrapper around
 * `@capacitor-community/stripe`. Handles:
 *   1. One-time SDK initialisation with the publishable key + merchant id
 *      (idempotent, safe to call before every payment).
 *   2. Payment Sheet creation (backend produces the PaymentIntent via
 *      /client/bookings/{id}/payment-intent) and presentation.
 *   3. Card enrolment via SetupIntent (add a card without paying — used
 *      by the "Mes cartes" screen for 1-tap future bookings).
 *
 * On the WEB (dev browser preview, non-Capacitor) the native plugin is
 * a no-op — callers should fall back to the existing Checkout redirect
 * flow via `openStripeCheckoutInApp`.
 */
import { Capacitor } from "@capacitor/core";
import { Stripe, PaymentSheetEventsEnum, PaymentFlowEventsEnum } from "@capacitor-community/stripe";
import { api } from "@/lib/api";

const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
// Merchant Apple Pay unique partagé Saor + Price (le popup Apple Pay
// affiche "Saor" mais le compte Stripe reste le même, plus simple à opérer).
const MERCHANT_ID = "merchant.com.saor.pro";
const PRICE_MERCHANT_DISPLAY = "Price";

let _initialized = false;

export function isNativeStripeAvailable() {
  // The plugin only exposes native functionality on iOS + Android
  // Capacitor runtimes. Everywhere else (Safari desktop, dev preview,
  // PWA installs) we must fall back to Checkout redirects.
  return Capacitor.isNativePlatform();
}

async function ensureInitialized() {
  if (_initialized) return;
  if (!PUBLISHABLE_KEY) {
    throw new Error("REACT_APP_STRIPE_PUBLISHABLE_KEY manquant");
  }
  await Stripe.initialize({ publishableKey: PUBLISHABLE_KEY });
  _initialized = true;
}

/**
 * Price — Recharge portefeuille (native).
 * Copie exacte de la config Saor `payBookingNative` (sheet branch).
 */
export async function topupPriceWalletNative(amountEur) {
  if (!isNativeStripeAvailable()) {
    throw new Error("Native Stripe indisponible sur cette plateforme");
  }
  await ensureInitialized();
  const { data } = await api.post("/price/driver/wallet/topup-intent", {
    amount_eur: Number(amountEur),
  });
  const clientSecret = data?.client_secret;
  if (!clientSecret) throw new Error("PaymentIntent introuvable");
  await Stripe.createPaymentSheet({
    paymentIntentClientSecret: clientSecret,
    merchantDisplayName: PRICE_MERCHANT_DISPLAY,
    enableApplePay: true,
    applePayMerchantId: MERCHANT_ID,
    enableGooglePay: true,
    GooglePayIsTesting: false,
    countryCode: "FR",
    style: "alwaysLight",
  });
  const r = await Stripe.presentPaymentSheet();
  if (r.paymentResult === PaymentSheetEventsEnum.Completed) return { success: true };
  if (r.paymentResult === PaymentSheetEventsEnum.Canceled) return { success: false, reason: "cancelled" };
  return { success: false, reason: "failed", error: r };
}

/**
 * Price — Paiement d'une demande de course (native).
 * Copie exacte de la config Saor.
 */
export async function payPriceRideNative(rideRequestId) {
  if (!isNativeStripeAvailable()) {
    throw new Error("Native Stripe indisponible sur cette plateforme");
  }
  await ensureInitialized();
  const { data } = await api.post(`/price/ride-requests/${rideRequestId}/payment-intent`);
  const clientSecret = data?.client_secret;
  if (!clientSecret) throw new Error("PaymentIntent introuvable");
  await Stripe.createPaymentSheet({
    paymentIntentClientSecret: clientSecret,
    merchantDisplayName: PRICE_MERCHANT_DISPLAY,
    enableApplePay: true,
    applePayMerchantId: MERCHANT_ID,
    enableGooglePay: true,
    GooglePayIsTesting: false,
    countryCode: "FR",
    style: "alwaysLight",
  });
  const r = await Stripe.presentPaymentSheet();
  if (r.paymentResult === PaymentSheetEventsEnum.Completed) return { success: true };
  if (r.paymentResult === PaymentSheetEventsEnum.Canceled) return { success: false, reason: "cancelled" };
  return { success: false, reason: "failed", error: r };
}

/**
 * Saor — Paiement d'une réservation VTC (native). Apple Pay via
 * `merchant.com.saor.pro` (Saor App ID `com.saor.monapp`).
 */
export async function payBookingNative(bookingId, savedPaymentMethodId = null) {
  if (!isNativeStripeAvailable()) {
    throw new Error("Native Stripe indisponible sur cette plateforme");
  }
  await ensureInitialized();

  let data;
  try {
    const resp = await api.post(`/client/bookings/${bookingId}/payment-intent`, {
      payment_method_id: savedPaymentMethodId || undefined,
    });
    data = resp.data;
  } catch (err) {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail || err?.message || "";
    if (status === 409) {
      return { success: false, reason: "stale_card", error: new Error(detail) };
    }
    throw err;
  }
  const clientSecret = data?.client_secret;
  if (!clientSecret) {
    throw new Error("PaymentIntent introuvable");
  }

  // ─── 1-tap branch : off_session confirm was done server-side ───
  if (savedPaymentMethodId) {
    if (data.status === "requires_capture" || data.status === "succeeded") {
      return { success: true };
    }
    if (data.requires_action) {
      await Stripe.createPaymentSheet({
        paymentIntentClientSecret: clientSecret,
        merchantDisplayName: "Saor",
        countryCode: "FR",
        style: "alwaysLight",
      });
      const r = await Stripe.presentPaymentSheet();
      if (r.paymentResult === PaymentSheetEventsEnum.Completed) return { success: true };
      if (r.paymentResult === PaymentSheetEventsEnum.Canceled) return { success: false, reason: "cancelled" };
      return { success: false, reason: "failed", error: r };
    }
    return { success: false, reason: "failed", error: new Error(data.status || "unknown") };
  }

  // ─── Sheet branch : Apple Pay / new card entry ───
  await Stripe.createPaymentSheet({
    paymentIntentClientSecret: clientSecret,
    merchantDisplayName: "Saor",
    enableApplePay: true,
    applePayMerchantId: MERCHANT_ID,
    enableGooglePay: true,
    GooglePayIsTesting: false,
    countryCode: "FR",
    style: "alwaysLight",
  });
  const result = await Stripe.presentPaymentSheet();
  if (result.paymentResult === PaymentSheetEventsEnum.Completed) return { success: true };
  if (result.paymentResult === PaymentSheetEventsEnum.Canceled) return { success: false, reason: "cancelled" };
  return { success: false, reason: "failed", error: result };
}

/**
 * Enrol a new card without charging (Saor "Mes cartes" screen).
 */
export async function addCardNative() {
  if (!isNativeStripeAvailable()) {
    throw new Error("Native Stripe indisponible sur cette plateforme");
  }
  await ensureInitialized();

  const { data } = await api.post("/client/payment-methods/setup-intent");
  const clientSecret = data?.client_secret;
  if (!clientSecret) throw new Error("SetupIntent introuvable");

  await Stripe.createPaymentFlow({
    setupIntentClientSecret: clientSecret,
    merchantDisplayName: "Saor",
    enableApplePay: true,
    applePayMerchantId: MERCHANT_ID,
    enableGooglePay: true,
    GooglePayIsTesting: false,
    countryCode: "FR",
    style: "alwaysLight",
  });
  await Stripe.presentPaymentFlow();
  const result = await Stripe.confirmPaymentFlow();
  if (result.paymentResult === PaymentFlowEventsEnum.Completed) return { success: true };
  if (result.paymentResult === PaymentFlowEventsEnum.Canceled) return { success: false, reason: "cancelled" };
  return { success: false, reason: "failed", error: result };
}
